/**
 * Quiet "Show N more / Show less" row - same family as Browse
 * `.browse-show-hidden-link` (Eye + italic muted).
 *
 * Gallery: /?test=maater-ui#list-overflow
 * Helpers: @/lib/list-overflow
 */
import type { ReactNode } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ShowOverflowLinkProps = {
  expanded: boolean
  /** Count still hidden when collapsed (drives default labels). */
  overflowCount: number
  onToggle: () => void
  /** Override collapsed label (default: Show N more …). */
  showMoreLabel?: string
  showLessLabel?: string
  className?: string
  'data-testid'?: string
  /** Optional leading icon override when collapsed. */
  icon?: ReactNode
}

export function ShowOverflowLink({
  expanded,
  overflowCount,
  onToggle,
  showMoreLabel,
  showLessLabel = 'Show less',
  className,
  'data-testid': testId,
  icon,
}: ShowOverflowLinkProps) {
  if (overflowCount <= 0) return null

  const label = expanded
    ? showLessLabel
    : (showMoreLabel ??
      (overflowCount === 1 ? 'Show 1 more' : `Show ${overflowCount} more`))

  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      className={cn(
        'flex w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-left text-xs italic',
        'text-muted-foreground transition-colors',
        'hover:bg-accent/60 hover:text-foreground',
        className,
      )}
    >
      {icon ??
        (expanded ? (
          <EyeOff className="h-3 w-3 shrink-0" aria-hidden />
        ) : (
          <Eye className="h-3 w-3 shrink-0" aria-hidden />
        ))}
      <span>{label}</span>
    </button>
  )
}
